import { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import {
  Plus, Edit, Trash2, Users, MessageSquare, Mail, Phone, Clock, Info,
  Tag, Globe, CheckCircle2, XCircle, Settings2, ShieldOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DAYS, fetchVehicles, mockVehicles, normalizeDriverContactRule } from '@/data/api';
import { useDriverRules } from '@/contexts/DriverRulesContext';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from 'sonner';
import { api, departmentEndpoints, driverContactRuleEndpoints, getApiErrorMessage } from '@/lib/api';
import { PERMISSIONS } from '@/lib/permissions';

const CHANNEL_META = {
  sms:   { label: 'SMS',      Icon: MessageSquare, color: '#3b82f6', bg: '#dbeafe' },
  email: { label: 'Email',    Icon: Mail,          color: '#8b5cf6', bg: '#ede9fe' },
  call:  { label: 'Chamada',  Icon: Phone,         color: '#ef4444', bg: '#fee2e2' },
};

const DAY_KEY_TO_ID = {
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
  sun: 7,
};

const collectionPayload = (payload, key) => {
  const source = payload?.data ?? payload;
  if (Array.isArray(source)) return source;
  if (Array.isArray(source?.data)) return source.data;
  if (Array.isArray(source?.[key])) return source[key];
  return [];
};

const normalizeDepartments = (payload) =>
  collectionPayload(payload, 'departments')
    .map((department, index) => ({
      id: String(department?.id ?? department?.department_id ?? department?.uuid ?? index + 1),
      name: department?.name ?? department?.label ?? `Departamento ${index + 1}`,
    }))
    .filter(department => department.id && department.name);

const get_department_label = (departments, id, fallback = '') =>
  departments.find(department => String(department.id) === String(id))?.name || fallback;

const get_department_id_by_name = (departments, name) =>
  departments.find(department => department.name === name)?.id || '';

const to_api_department_id = (id) => {
  const numericId = Number(id);
  return Number.isNaN(numericId) ? id : numericId;
};

const timeToHourMinute = (value, fallback = '') => {
  if (!value) return fallback;
  const match = String(value).match(/^(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : String(value);
};

const channelPayload = ([channel, config]) => ({
  channel,
  mode: config.mode,
  uniform_start_at: config.mode === 'uniform' ? timeToHourMinute(config.start) : null,
  uniform_end_at: config.mode === 'uniform' ? timeToHourMinute(config.end) : null,
  status: config.enabled ? 1 : 0,
  day_ids: config.days.map(day => DAY_KEY_TO_ID[day]).filter(Boolean),
  per_day: config.days.map(day => ({
    day_id: DAY_KEY_TO_ID[day],
    start_time_at: timeToHourMinute(config.perDay?.[day]?.start ?? config.start),
    end_time_at: timeToHourMinute(config.perDay?.[day]?.end ?? config.end),
    status: config.mode === 'per-day' ? (config.perDay?.[day]?.enabled ? 1 : 0) : 1,
  })).filter(day => day.day_id),
});

const rulePayload = ({name, description, scope, category, channels}) => ({
  name: name.trim(),
  description: description.trim() || null,
  scope,
  department_id: scope === 'general' ? null : to_api_department_id(category),
  channels: Object.entries(channels).map(channelPayload),
});

const cloneRule = (r) => JSON.parse(JSON.stringify(r));

const emptyChannel = () => ({
  enabled: true, mode: 'uniform',
  days: ['mon','tue','wed','thu','fri'],
  start: '08:00', end: '18:00', perDay: {},
});

const formatDaysShort = (days) => {
  if (days.length === 7) return 'Todos os dias';
  if (days.length === 5 && ['mon','tue','wed','thu','fri'].every(d => days.includes(d))) return 'Dias úteis';
  if (days.length === 2 && days.includes('sat') && days.includes('sun')) return 'Fins-de-semana';
  return days.map(d => DAYS.find(x => x.key === d)?.short).filter(Boolean).join(', ');
};

// -------------------- Channel Editor (used in dialog) --------------------
const ChannelEditor = ({ channelKey, channel, onChange }) => {
  const meta = CHANNEL_META[channelKey];
  const Icon = meta.Icon;

  const toggleDay = (d) => {
    const days = channel.days.includes(d) ? channel.days.filter(x => x !== d) : [...channel.days, d];
    onChange({ ...channel, days });
  };
  const setDaysPreset = (preset) => {
    const all = ['mon','tue','wed','thu','fri','sat','sun'];
    const weekdays = ['mon','tue','wed','thu','fri'];
    const weekend = ['sat','sun'];
    onChange({ ...channel, days: preset === 'all' ? all : preset === 'weekdays' ? weekdays : weekend });
  };
  const setPerDayField = (dayKey, field, value) => {
    onChange({ ...channel, perDay: { ...channel.perDay, [dayKey]: { ...(channel.perDay[dayKey] || { enabled: true, start: '08:00', end: '18:00' }), [field]: value } } });
  };

  return (
    <Card className={`p-4 transition-all ${channel.enabled ? '' : 'opacity-60'}`} data-testid={`channel-editor-${channelKey}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: meta.bg }}>
            <Icon size={18} style={{ color: meta.color }} />
          </div>
          <div>
            <p className="font-secondary font-bold text-slate-900">{meta.label}</p>
            <p className="text-xs text-slate-500">
              {channel.enabled ? 'Ativo — configurar horário' : 'Inativo — nunca envia'}
            </p>
          </div>
        </div>
        <Switch checked={channel.enabled} onCheckedChange={v => onChange({ ...channel, enabled: v })}
                data-testid={`channel-toggle-${channelKey}`} />
      </div>

      {channel.enabled && (
        <div className="space-y-4">
          {/* Dias da semana */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Dias da semana</Label>
              <div className="flex gap-1">
                <button type="button" onClick={() => setDaysPreset('weekdays')}
                        className="text-[10px] px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50">Dias úteis</button>
                <button type="button" onClick={() => setDaysPreset('weekend')}
                        className="text-[10px] px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50">Fim-de-semana</button>
                <button type="button" onClick={() => setDaysPreset('all')}
                        className="text-[10px] px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50">Todos</button>
              </div>
            </div>
            <div className="flex gap-1.5">
              {DAYS.map(d => {
                const active = channel.days.includes(d.key);
                return (
                  <button key={d.key} type="button" onClick={() => toggleDay(d.key)}
                    data-testid={`day-${channelKey}-${d.key}`}
                    className={`flex-1 py-2 rounded-md text-xs font-semibold transition-all ${
                      active ? 'text-white shadow-sm' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                    }`}
                    style={active ? { backgroundColor: meta.color } : {}}>
                    {d.short}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mode toggle */}
          <div className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-md">
            <Label className="text-xs text-slate-700 flex-1">Horário diferente por dia?</Label>
            <Switch
              checked={channel.mode === 'per-day'}
              onCheckedChange={v => onChange({ ...channel, mode: v ? 'per-day' : 'uniform' })}
              data-testid={`mode-toggle-${channelKey}`}
            />
          </div>

          {/* Horário */}
          {channel.mode === 'uniform' ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Início</Label>
                <Input type="time" value={channel.start} onChange={e => onChange({ ...channel, start: e.target.value })}
                       data-testid={`time-start-${channelKey}`} />
              </div>
              <div>
                <Label className="text-xs">Fim</Label>
                <Input type="time" value={channel.end} onChange={e => onChange({ ...channel, end: e.target.value })}
                       data-testid={`time-end-${channelKey}`} />
              </div>
            </div>
          ) : (
            <div className="space-y-2 max-h-[240px] overflow-y-auto">
              {channel.days.map(dk => {
                const day = DAYS.find(d => d.key === dk);
                const pd = channel.perDay[dk] || { enabled: true, start: '08:00', end: '18:00' };
                return (
                  <div key={dk} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                    <span className="text-xs font-semibold text-slate-700 w-16">{day.label}</span>
                    <Switch checked={pd.enabled} onCheckedChange={v => setPerDayField(dk, 'enabled', v)} />
                    {pd.enabled && (
                      <>
                        <Input type="time" className="h-8 text-xs" value={pd.start}
                               onChange={e => setPerDayField(dk, 'start', e.target.value)} />
                        <span className="text-xs text-slate-400">→</span>
                        <Input type="time" className="h-8 text-xs" value={pd.end}
                               onChange={e => setPerDayField(dk, 'end', e.target.value)} />
                      </>
                    )}
                  </div>
                );
              })}
              {channel.days.length === 0 && <p className="text-xs text-slate-400 text-center py-3">Selecione primeiro os dias da semana.</p>}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

// -------------------- Rule Card --------------------
const RuleCard = ({ rule, onEdit, onDelete, vehicles, categories, canUpdate, canDelete }) => {
  const isGeneral = rule.scope === 'general';
  const categoryLabel = isGeneral ? '' : get_department_label(categories, rule.department_id, rule.category || 'Categoria');
  const affected = isGeneral
    ? vehicles.filter(v => !v.driverContactRuleId && !categories.some(c => false))
    : vehicles.filter(v => v.driverContactRuleId === rule.id || (!v.driverContactRuleId && v.category === categoryLabel));

  const activeChannels = Object.entries(rule.channels).filter(([, c]) => c.enabled);

  return (
    <Card className={`p-5 ${isGeneral ? 'border-blue-300 bg-gradient-to-br from-blue-50 to-white' : ''}`}
          data-testid={`rule-card-${rule.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isGeneral ? 'bg-blue-600' : 'bg-slate-700'}`}>
            {isGeneral ? <Globe className="text-white" size={20} /> : <Tag className="text-white" size={20} />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-secondary font-bold text-slate-900 text-base leading-tight">{rule.name}</h3>
              <Badge className={isGeneral ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}>
                {isGeneral ? 'Geral' : categoryLabel}
              </Badge>
            </div>
            {rule.description && <p className="text-xs text-slate-500 mt-1">{rule.description}</p>}
          </div>
        </div>
        {(canUpdate || (!isGeneral && canDelete)) && (
          <div className="flex items-center gap-1">
          {canUpdate && (
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onEdit(rule)}
                    data-testid={`edit-rule-${rule.id}`}><Edit size={14} className="text-slate-600" /></Button>
          )}
          {!isGeneral && canDelete && (
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onDelete(rule.id)}
                    data-testid={`delete-rule-${rule.id}`}><Trash2 size={14} className="text-red-500" /></Button>
          )}
          </div>
        )}
      </div>

      {/* Canais */}
      <div className="space-y-2 mb-4">
        {Object.entries(rule.channels).map(([key, ch]) => {
          const meta = CHANNEL_META[key];
          const Icon = meta.Icon;
          return (
            <div key={key} className={`flex items-center gap-2 text-xs ${ch.enabled ? 'text-slate-700' : 'text-slate-400'}`}>
              <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: ch.enabled ? meta.bg : '#f1f5f9' }}>
                <Icon size={11} style={{ color: ch.enabled ? meta.color : '#94a3b8' }} />
              </div>
              {ch.enabled ? (
                <>
                  <span className="font-medium w-14">{meta.label}</span>
                  <Clock size={10} className="text-slate-400" />
                  <span className="truncate">{formatDaysShort(ch.days)}</span>
                  <span className="text-slate-400">·</span>
                  <span className="font-mono">
                    {ch.mode === 'per-day' ? 'variável' : `${ch.start}–${ch.end}`}
                  </span>
                </>
              ) : (
                <>
                  <span className="font-medium w-14">{meta.label}</span>
                  <XCircle size={11} className="text-slate-300" />
                  <span className="italic">Desativado</span>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Canais ativos count */}
      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        <Badge variant="outline" className="text-[10px]">
          <CheckCircle2 size={10} className="mr-1 text-emerald-600" />
          {activeChannels.length} canal(is) ativo(s)
        </Badge>
        <span className="text-xs text-slate-500 flex items-center gap-1">
          <Users size={12} />
          {isGeneral ? 'Fallback para todos' : `${affected.length} veículo(s)`}
        </span>
      </div>
    </Card>
  );
};

// -------------------- Main Page --------------------
export const DriverContactRulesPage = () => {
  const { rules, setRules, rulesLoading, rulesError } = useDriverRules();
  const { can } = usePermissions();

  const canReadDriverRules = can(PERMISSIONS.DRIVER_RULE_READ);
  const canCreateDriverRules = can(PERMISSIONS.DRIVER_RULE_CREATE);
  const canUpdateDriverRules = can(PERMISSIONS.DRIVER_RULE_UPDATE);
  const canDeleteDriverRules = can(PERMISSIONS.DRIVER_RULE_DELETE);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState('category');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [vehicles, setVehicles] = useState(mockVehicles);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState('');
  const [channels, setChannels] = useState({ sms: emptyChannel(), email: emptyChannel(), call: emptyChannel() });

  useEffect(() => {
    if (!canReadDriverRules) {
      setVehicles(mockVehicles);
      return undefined;
    }

    let cancelled = false;

    const loadVehicles = async () => {
      try {
        const nextVehicles = await fetchVehicles();
        if (!cancelled) setVehicles(nextVehicles);
      } catch {
        if (!cancelled) setVehicles(mockVehicles);
      }
    };

    loadVehicles();

    return () => {
      cancelled = true;
    };
  }, [canReadDriverRules]);

  useEffect(() => {
    if (!canReadDriverRules) {
      setCategories([]);
      setCategoriesLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadDepartments = async () => {
      setCategoriesLoading(true);
      setCategoriesError('');

      try {
        const response = await api.get(departmentEndpoints.list);
        const nextCategories = normalizeDepartments(response.data);
        if (!cancelled) {
          setCategories(nextCategories);
          setCategory(current => current || nextCategories[0]?.id || '');
        }
      } catch (error) {
        if (!cancelled) {
          setCategories([]);
          setCategoriesError(getApiErrorMessage(error, 'Não foi possível carregar as categorias.'));
        }
      } finally {
        if (!cancelled) setCategoriesLoading(false);
      }
    };

    loadDepartments();

    return () => {
      cancelled = true;
    };
  }, [canReadDriverRules]);

  const openCreate = () => {
    if (!canCreateDriverRules) {
      toast.error('Sem permissão para criar regras de contacto com condutor.');
      return;
    }
    setEditing(null);
    setName(''); setDescription(''); setScope('category'); setCategory(categories[0]?.id || '');
    setChannels({ sms: emptyChannel(), email: emptyChannel(), call: { ...emptyChannel(), enabled: false } });
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    if (!canUpdateDriverRules) {
      toast.error('Sem permissão para editar regras de contacto com condutor.');
      return;
    }
    setEditing(r);
    setName(r.name); setDescription(r.description || '');
    setScope(r.scope);
    setCategory(r.department_id ? String(r.department_id) : get_department_id_by_name(categories, r.category) || categories[0]?.id || '');
    setChannels(cloneRule(r).channels);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!canDeleteDriverRules) {
      toast.error('Sem permissão para eliminar regras de contacto com condutor.');
      return;
    }
    const rule = rules.find(r => r.id === id);
    if (!rule) return;
    if (!window.confirm(`Eliminar regra "${rule.name}"?`)) return;

    setSubmitting(true);
    try {
      await api.post(driverContactRuleEndpoints.delete, {
        id: rule.id,
        uuid: rule.uuid,
      });
      setRules(prev => prev.filter(r => r.id !== id));
      toast.success('Regra removida.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Não foi possível remover a regra.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async () => {
    if (editing && !canUpdateDriverRules) {
      toast.error('Sem permissão para editar regras de contacto com condutor.');
      return;
    }
    if (!editing && !canCreateDriverRules) {
      toast.error('Sem permissão para criar regras de contacto com condutor.');
      return;
    }
    if (!name.trim()) { toast.error('Nome é obrigatório.'); return; }
    if (scope === 'category' && !category) { toast.error('Selecione uma categoria.'); return; }
    if (scope === 'category' && !editing) {
      // disallow duplicate category rules (to keep hierarchy clean)
      const exists = rules.some(r => r.scope === 'category' && String(r.department_id) === String(category));
      const categoryLabel = get_department_label(categories, category, category);
      if (exists) { toast.error(`Já existe uma regra para a categoria "${categoryLabel}". Edite-a em vez de criar nova.`); return; }
    }
    if (scope === 'general' && !editing && generalRule) {
      toast.error('Já existe uma regra geral. Edite-a em vez de criar nova.');
      return;
    }
    // Validate enabled channels have at least 1 day
    for (const [k, c] of Object.entries(channels)) {
      if (c.enabled && c.days.length === 0) {
        toast.error(`${CHANNEL_META[k].label}: selecione pelo menos 1 dia.`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = rulePayload({name, description, scope, category, channels});
      if (editing) {
        const response = await api.post(driverContactRuleEndpoints.update, {
          id: editing.id,
          uuid: editing.uuid,
          ...payload,
        });
        const updated = normalizeDriverContactRule(response.data?.data ?? response.data?.rule ?? response.data, {...editing, ...payload});
        setRules(prev => prev.map(r => r.id === editing.id ? updated : r));
        toast.success(`Regra "${updated.name}" atualizada.`);
      } else {
        const response = await api.post(driverContactRuleEndpoints.create, payload);
        const created = normalizeDriverContactRule(response.data?.data ?? response.data?.rule ?? response.data, {
          id: Math.max(...rules.map(r => r.id), 0) + 1,
          ...payload,
        });
        setRules(prev => [...prev, created]);
        toast.success(`Regra "${created.name}" criada.`);
      }
      setDialogOpen(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Não foi possível guardar a regra.'));
    } finally {
      setSubmitting(false);
    }
  };

  const generalRule = rules.find(r => r.scope === 'general');
  const categoryRules = rules.filter(r => r.scope === 'category');

  if (!canReadDriverRules) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Card className="p-8 text-center max-w-md">
            <div className="w-14 h-14 rounded-2xl bg-red-100 mx-auto mb-4 flex items-center justify-center">
              <ShieldOff className="text-red-600" size={28} />
            </div>
            <h1 className="text-xl font-secondary font-bold text-slate-900">Acesso Negado</h1>
            <p className="text-sm text-slate-600 mt-2">
              Não tem permissão para consultar regras de contacto com condutor.
            </p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="driver-rules-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Regras Tags com responsável</h1>
            <p className="text-slate-600 font-primary mt-1">
              Defina como o sistema contacta o responsável da tag/veículo quando há uma ocorrência — canais, dias e horários.
            </p>
          </div>
          {canCreateDriverRules && (
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={openCreate} disabled={rulesLoading || submitting} data-testid="new-rule-btn">
              <Plus size={18} className="mr-2" />Nova regra
            </Button>
          )}
        </div>

        {/* Info card */}
        <Card className="p-4 border-blue-200 bg-blue-50 flex items-start gap-3">
          <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
          <div>
            <p className="text-sm font-medium text-blue-900">
              Hierarquia: <strong>Regra do veículo</strong> → <strong>Regra da categoria</strong> → <strong>Regra Geral</strong> (fallback)
            </p>
            <p className="text-xs text-blue-700 mt-0.5">
              A regra mais específica tem prioridade. Ao criar um veículo/equipamento pode escolher usar a herdada ou uma regra específica.
            </p>
          </div>
        </Card>

        {rulesError && (
          <Card className="p-4 border-red-200 bg-red-50 text-sm text-red-700">
            {rulesError}
          </Card>
        )}

        {/* Regra Geral */}
        {generalRule && (
          <div>
            <p className="text-xs font-secondary font-bold uppercase tracking-wider text-slate-500 mb-2">Regra Geral (fallback)</p>
            <RuleCard
              rule={generalRule}
              onEdit={openEdit}
              onDelete={handleDelete}
              vehicles={vehicles}
              categories={categories}
              canUpdate={canUpdateDriverRules}
              canDelete={canDeleteDriverRules}
            />
          </div>
        )}

        {/* Regras por Categoria */}
        <div>
          <p className="text-xs font-secondary font-bold uppercase tracking-wider text-slate-500 mb-2">
            Regras por Categoria ({categoryRules.length})
          </p>
          {rulesLoading ? (
            <Card className="p-8 text-center text-slate-500">
              <Settings2 className="mx-auto mb-2 text-slate-300" size={40} />
              <p className="text-sm">A carregar regras...</p>
            </Card>
          ) : categoryRules.length === 0 ? (
            <Card className="p-8 text-center text-slate-500">
              <Settings2 className="mx-auto mb-2 text-slate-300" size={40} />
              <p className="text-sm">Sem regras específicas por categoria.</p>
              <p className="text-xs text-slate-400 mt-1">Use "Nova regra por categoria" para criar uma.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {categoryRules.map(r => (
                <RuleCard
                  key={r.id}
                  rule={r}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  vehicles={vehicles}
                  categories={categories}
                  canUpdate={canUpdateDriverRules}
                  canDelete={canDeleteDriverRules}
                />
              ))}
            </div>
          )}
        </div>

        {/* ============ Dialog criar/editar ============ */}
        <Dialog
          open={dialogOpen && (editing ? canUpdateDriverRules : canCreateDriverRules)}
          onOpenChange={(open) => setDialogOpen(open && (editing ? canUpdateDriverRules : canCreateDriverRules))}
        >
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editing ? <Edit className="text-blue-600" size={20} /> : <Plus className="text-blue-600" size={20} />}
                {editing ? 'Editar Regra' : 'Nova Regra com Condutor'}
              </DialogTitle>
              <DialogDescription>Configure canais, dias e horários de contacto ao condutor.</DialogDescription>
            </DialogHeader>

            <div className="space-y-5 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input value={name} onChange={e => setName(e.target.value)}
                         placeholder="Ex: Frota Elétrica — Horário Alargado"
                         data-testid="rule-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Escopo</Label>
                  {editing?.scope === 'general' ? (
                    <div className="h-10 flex items-center px-3 bg-slate-50 rounded-md border border-slate-200 text-sm">
                      <Globe size={14} className="mr-2 text-blue-600" />Regra Geral (não alterável)
                    </div>
                  ) : (
                    <Select value={scope} onValueChange={setScope}>
                      <SelectTrigger data-testid="scope-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">Geral (fallback)</SelectItem>
                        <SelectItem value="category">Por categoria</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              {scope === 'category' && (
                <div className="space-y-2">
                  <Label>Categoria *</Label>
                  <Select value={category} onValueChange={setCategory} disabled={!!editing || categoriesLoading || categories.length === 0}>
                    <SelectTrigger data-testid="category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categoriesError && <div className="p-3 text-xs text-red-600">{categoriesError}</div>}
                      {!categoriesError && categories.length === 0 && (
                        <div className="p-3 text-xs text-slate-500">{categoriesLoading ? 'A carregar categorias...' : 'Sem categorias disponíveis.'}</div>
                      )}
                      {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {editing && <p className="text-xs text-slate-500">A categoria não pode ser alterada depois de criada. Crie uma nova regra se necessário.</p>}
                </div>
              )}

              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
                          placeholder="Notas sobre quando esta regra se aplica..." />
              </div>

              <div className="space-y-3 pt-2 border-t border-slate-200">
                <Label className="text-sm font-semibold">Canais de contacto</Label>
                <p className="text-xs text-slate-500 -mt-1">Cada canal tem o seu próprio horário. Desative o que não quer usar.</p>
                <div className="space-y-3">
                  {['sms', 'email', 'call'].map(k => (
                    <ChannelEditor key={k} channelKey={k} channel={channels[k]}
                                    onChange={(c) => setChannels(p => ({ ...p, [k]: c }))} />
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>Cancelar</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSave} disabled={submitting || (editing ? !canUpdateDriverRules : !canCreateDriverRules)} data-testid="save-rule-btn">
                {submitting ? 'A guardar...' : editing ? 'Guardar Alterações' : <><Plus size={16} className="mr-1" />Criar Regra</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

